import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Copy, DeviceMobile, Upload, CheckCircle, Clock, Warning } from "@phosphor-icons/react";

/**
 * Manual UPI Payment card — user pays to a business UPI ID and uploads
 * the payment screenshot for admin verification.
 *
 * Props:
 *  - order: backend response { provider:"manual_upi", reference, upi_id, upi_name, upi_url, amount }
 *  - kind: "booking" | "rental"
 *  - refId: the booking/rental id
 *  - entityStatus: current manual_upi_status ("awaiting_proof" | "awaiting_verification" | "verified" | "rejected")
 *  - onSubmitted: callback fired after user uploads proof successfully
 */
export default function ManualUPICheckout({ order, kind, refId, entityStatus, onSubmitted }) {
  const [proofFile, setProofFile] = useState(null);
  const [payerRef, setPayerRef] = useState("");
  const [uploading, setUploading] = useState(false);

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=380x380&margin=8&data=${encodeURIComponent(order.upi_url)}`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const submitProof = async () => {
    if (!proofFile) { toast.error("Please attach your payment screenshot"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", proofFile);
      const { data: uploaded } = await api.post("/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post("/payments/manual-upi/proof", {
        kind,
        ref_id: refId,
        file_id: uploaded.id,
        payer_upi_ref: payerRef || null,
      });
      toast.success("Payment proof submitted — admin will verify shortly");
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // ---------- Different status displays ----------
  if (entityStatus === "verified") {
    return (
      <div className="p-8 bg-emerald-50 border border-emerald-200 rounded-2xl text-center" data-testid="upi-verified">
        <CheckCircle size={56} weight="fill" className="text-emerald-600 mx-auto mb-4" />
        <h3 className="font-serif text-2xl text-midnight mb-2">Payment verified</h3>
        <p className="text-sm text-emerald-800">Your {kind} is confirmed. See you soon!</p>
      </div>
    );
  }

  if (entityStatus === "awaiting_verification") {
    return (
      <div className="p-8 bg-amber-50 border border-amber-200 rounded-2xl text-center" data-testid="upi-pending-verify">
        <Clock size={56} weight="fill" className="text-amber-600 mx-auto mb-4" />
        <h3 className="font-serif text-2xl text-midnight mb-2">Verification in progress</h3>
        <p className="text-sm text-amber-800 mb-2">
          We received your payment proof. Our team will verify it within <b>2-4 hours</b> (during business hours).
        </p>
        <p className="text-xs text-amber-700">
          Reference: <code className="bg-white px-2 py-1 rounded font-mono">{order.reference}</code>
        </p>
      </div>
    );
  }

  if (entityStatus === "rejected") {
    return (
      <div className="p-8 bg-red-50 border border-red-200 rounded-2xl" data-testid="upi-rejected">
        <div className="flex items-center gap-3 mb-3">
          <Warning size={32} weight="fill" className="text-red-600" />
          <h3 className="font-serif text-xl text-midnight">Payment proof rejected</h3>
        </div>
        <p className="text-sm text-red-800 mb-4">
          Your previous proof could not be verified. Please pay again to the UPI ID below and re-upload a clear screenshot.
        </p>
        {/* fall through to show payment UI again */}
        <PayForm />
      </div>
    );
  }

  // Default: awaiting_proof — show payment UI
  return <PayForm />;

  function PayForm() {
    return (
      <div className="space-y-5" data-testid="upi-manual-pay">
        {/* Hero card with QR + UPI ID */}
        <div className="p-6 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 border border-emerald-200 rounded-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <DeviceMobile size={22} weight="fill" className="text-emerald-700" />
            </div>
            <div>
              <p className="text-[10px] tracking-widest uppercase text-emerald-700">Step 1 · Pay via UPI</p>
              <p className="font-serif text-xl text-midnight">Scan or open UPI app</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-4">
            <div className="text-center">
              <img src={qrUrl} alt="UPI QR code" className="mx-auto rounded-xl border-4 border-white shadow-md" width={280} height={280} data-testid="upi-qr" />
              <p className="text-[11px] text-neutral-500 mt-2">GPay · PhonePe · Paytm · BHIM · any UPI app</p>
            </div>

            <div className="space-y-3">
              <div className="p-3 bg-white border border-black/5 rounded-xl">
                <p className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1">Pay to</p>
                <div className="flex items-center justify-between gap-2">
                  <code className="font-mono text-sm text-midnight break-all">{order.upi_id}</code>
                  <button onClick={() => copy(order.upi_id, "UPI ID")} className="p-1 text-gold hover:text-midnight flex-shrink-0" data-testid="copy-upi">
                    <Copy size={16} />
                  </button>
                </div>
                <p className="text-xs text-neutral-600 mt-1">{order.upi_name}</p>
              </div>

              <div className="p-3 bg-white border border-black/5 rounded-xl">
                <p className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1">Amount</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-serif text-2xl text-midnight">₹{Number(order.amount).toLocaleString("en-IN")}</p>
                  <button onClick={() => copy(String(order.amount), "Amount")} className="p-1 text-gold hover:text-midnight" data-testid="copy-amount">
                    <Copy size={16} />
                  </button>
                </div>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-[10px] tracking-widest uppercase text-amber-700 mb-1">⚠️ Reference (add in payment note)</p>
                <div className="flex items-center justify-between gap-2">
                  <code className="font-mono text-sm font-bold text-midnight tracking-widest">{order.reference}</code>
                  <button onClick={() => copy(order.reference, "Reference")} className="p-1 text-gold hover:text-midnight" data-testid="copy-reference">
                    <Copy size={16} />
                  </button>
                </div>
                <p className="text-[10px] text-amber-800 mt-1">Add this reference in the payment note. Helps us verify faster.</p>
              </div>

              <a href={order.upi_url}
                 className="block text-center bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-full text-sm tracking-widest uppercase transition-colors md:hidden"
                 data-testid="upi-app-link">
                <DeviceMobile size={14} weight="fill" className="inline mr-2" /> Open UPI app
              </a>
            </div>
          </div>
        </div>

        {/* Upload proof card */}
        <div className="p-6 bg-white border border-black/10 rounded-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
              <Upload size={20} weight="bold" className="text-gold" />
            </div>
            <div>
              <p className="text-[10px] tracking-widest uppercase text-gold">Step 2 · Upload proof</p>
              <p className="font-serif text-xl text-midnight">Send your payment screenshot</p>
            </div>
          </div>
          <p className="text-sm text-neutral-600 mb-4">
            After paying, share your UPI transaction reference (UTR) — we can verify in
            <b className="text-emerald-700"> under 30 seconds</b>. Screenshot is optional but helps if UTR is missing.
          </p>

          {/* Instant verify hero */}
          <div className={`mb-4 p-4 rounded-xl border-2 border-dashed transition-colors ${
            payerRef.length === 12 && /^\d{12}$/.test(payerRef)
              ? "border-emerald-400 bg-emerald-50"
              : "border-amber-300 bg-amber-50"
          }`} data-testid="utr-hero">
            <div className="flex items-center gap-3">
              <span className="text-xl">{payerRef.length === 12 && /^\d{12}$/.test(payerRef) ? "⚡" : "🔍"}</span>
              <div className="flex-1">
                <p className="text-[10px] tracking-widest uppercase text-neutral-700 font-medium">
                  {payerRef.length === 12 && /^\d{12}$/.test(payerRef) ? "Instant verify eligible" : "Enter UTR for instant verify"}
                </p>
                <p className="text-xs text-neutral-600">
                  {payerRef.length === 12 && /^\d{12}$/.test(payerRef)
                    ? "Great! Our team will approve within 30 seconds using your UTR."
                    : "Find the 12-digit UPI reference in your GPay / PhonePe / Paytm transaction detail."}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-1">
                UTR / UPI Reference <span className="normal-case text-emerald-700 font-semibold">— recommended</span>
              </label>
              <input value={payerRef}
                     onChange={(e) => setPayerRef(e.target.value.replace(/[^0-9]/g, "").slice(0, 12))}
                     placeholder="12-digit UPI reference (e.g. 421526789012)"
                     inputMode="numeric" maxLength={12}
                     className={`w-full border rounded-md p-3 text-sm font-mono tracking-widest ${
                       payerRef.length === 12 ? "border-emerald-400 bg-emerald-50" : "border-black/10"}`}
                     data-testid="upi-utr-input" />
              <p className="text-[10px] text-neutral-500 mt-1">
                {payerRef.length > 0 && payerRef.length < 12 ? `${12 - payerRef.length} more digit${12 - payerRef.length === 1 ? "" : "s"}…` : "GPay/PhonePe/Paytm → tap the transaction → 'View Details' → 12-digit reference"}
              </p>
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-1">
                Payment screenshot <span className="normal-case text-neutral-400">(optional if UTR provided)</span>
              </label>
              <input type="file" accept="image/*,application/pdf"
                     onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                     className="block w-full text-sm border border-black/10 rounded-lg p-2 file:mr-3 file:border-0 file:bg-midnight file:text-bone file:py-2 file:px-3 file:rounded-md hover:file:bg-midnight-hover"
                     data-testid="upi-proof-file" />
            </div>
            <Button onClick={submitProof}
                    disabled={uploading || (!proofFile && !(payerRef.length === 12 && /^\d{12}$/.test(payerRef)))}
                    className={`w-full py-6 text-white ${
                      payerRef.length === 12 && /^\d{12}$/.test(payerRef)
                        ? "bg-emerald-600 hover:bg-emerald-700"
                        : "bg-midnight hover:bg-midnight-hover"
                    }`}
                    data-testid="upi-submit-proof">
              {uploading ? "Uploading…" : payerRef.length === 12 && /^\d{12}$/.test(payerRef) ? (
                <span className="flex items-center gap-2"><span>⚡</span> Submit for instant verify</span>
              ) : (
                <span className="flex items-center gap-2"><Upload size={16} weight="bold" /> Submit for verification</span>
              )}
            </Button>
            <p className="text-[10px] text-center text-neutral-500">
              {payerRef.length === 12 && /^\d{12}$/.test(payerRef)
                ? "Verified within 30 seconds via UTR match"
                : "Verified within 2-4 hours by our team"}
            </p>
          </div>
        </div>
      </div>
    );
  }
}
